/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useModal } from '../../contexts/ModalContext'
import { useServiceContext } from '../../root'
import { Select } from '../Select/Select'
import Modal from '../Modal'
import { LoadingModal } from './LoadingModal'
import { usersApiService } from '../../services/api/users'
import type { DetailedWorkflow } from '../../services/api/workflows'

interface WorkflowQcApproversModalProps {
  workflowId: number
  workflow?: any
  onSuccess?: () => void
}

interface UserGroupOption {
  value: string
  label: string
  id: number
}

/**
 * Workflow QC Approvers Management Modal
 *
 * Manages QC approval sequence for QC_REVIEW workflows.
 * Based on lego/page/workflows/approvers/QcApprovers.jsx
 */
export const WorkflowQcApproversModal: React.FC<WorkflowQcApproversModalProps> = ({
  workflowId,
  workflow: initialWorkflow,
  onSuccess
}) => {
  const { t } = useTranslation()
  const { hideModal } = useModal()
  const queryClient = useQueryClient()
  const { api } = useServiceContext()

  const [loading, setLoading] = useState(true)
  const [workflow, setWorkflow] = useState<DetailedWorkflow | null>(initialWorkflow || null)
  const [selectedApprovers, setSelectedApprovers] = useState<string[]>([])
  const [nofAllValue, setNofAllValue] = useState<number | null>(null)
  const [usersGroupsOptions, setUsersGroupsOptions] = useState<UserGroupOption[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    loadData()
  }, [workflowId])

  const loadData = async () => {
    try {
      setLoading(true)

      // Load workflow details if not provided
      let wfData = initialWorkflow
      if (!wfData?.workflowNo) {
        wfData = await api.workflows.getWorkflow(workflowId)
      }

      setWorkflow(wfData)

      // Load users/groups options
      const usersGroupsData = await usersApiService.getAssembledUsersGroups(false)
      const options = usersGroupsData.merged.map((user: any) => ({
        value: user._uid,
        label: user.chName,
        id: user.id
      }))
      setUsersGroupsOptions(options)

      // Load approvers data
      const approversResponse = await api.workflows.getCRApprovers(wfData.id, true)

      // Transform API response to component format (array of _uid strings)
      const approversList: string[] = []
      approversResponse.approvers?.forEach((item: any) => {
        item?.taskTeams?.forEach((team: any) => {
          if (team?.user) {
            // Find the matching user option by id to get the _uid
            const userOption = options.find((opt) => opt.id === team.user.id)
            if (userOption) {
              approversList.push(userOption.value)
            }
          } else if (team?.group) {
            // Find the matching group option by id to get the _uid
            const groupOption = options.find((opt) => opt.id === team.group.id)
            if (groupOption) {
              approversList.push(groupOption.value)
            }
          }
        })
      })

      setSelectedApprovers(approversList)
      const nofAll = approversResponse.approvers?.[0]?.nofAllValue
      setNofAllValue(nofAll !== undefined && nofAll !== null ? nofAll : null)
      setLoading(false)
    } catch (error) {
      console.error('Failed to load workflow QC approvers:', error)
      setErrors({ submit: t('ERROR_LOADING_DATA') || 'Failed to load data' })
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation: At least one approver must be selected
    if (!selectedApprovers || selectedApprovers.length === 0) {
      setErrors({ approvers: t('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR') || 'At least one approver is required' })
      return
    }

    // Validation: N of All must be a valid integer >= 1
    if (!Number.isInteger(nofAllValue) || (nofAllValue !== null && nofAllValue < 1)) {
      setErrors({ nofAllValue: t('SOME_AT_ONCE_ERROR_MSG') || 'N of All must be at least 1' })
      return
    }

    try {
      setIsSubmitting(true)
      setErrors({})

      // Transform selected _uid values back to {user: {id}} or {group: {id}} format
      const taskTeams = selectedApprovers
        .map((uid) => {
          const option = usersGroupsOptions.find((opt) => opt.value === uid)
          if (!option) return null

          // Parse the _uid to determine if it's a user or group
          // _uid format is typically "USER-123" or "GROUP-456"
          const [type, id] = uid.split('-')
          return type === 'USER' ? { user: { id: parseInt(id) } } : { group: { id: parseInt(id) } }
        })
        .filter((item): item is { user: { id: number } } | { group: { id: number } } => item !== null)

      const updateData = {
        approvers: [
          {
            processingType: 'N_OF_ALL',
            nofAllValue: nofAllValue,
            taskTeamStepLabel: null,
            taskTeams: taskTeams
          }
        ]
      }

      await api.workflows.updateCRApprovers(workflowId, updateData, true)

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['workflows'] })
      queryClient.invalidateQueries({ queryKey: ['workflow', workflowId] })

      hideModal()

      if (onSuccess) {
        onSuccess()
      }
    } catch (error: any) {
      console.error('Failed to update workflow QC approvers:', error)
      setErrors({
        submit: error.response?.data?.message || t('CHANGE_REQUEST_EDIT_ERROR_MSG') || 'Failed to update'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    hideModal()
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleCancel()
    }
  }

  if (loading) {
    return (
      <LoadingModal title={t('WF_PHASE_QC_APPROVAL')} onBackdropClick={handleBackdropClick} onClose={handleCancel} />
    )
  }

  return (
    <Modal show={true} handleClose={handleCancel} title={t('WF_PHASE_QC_APPROVAL')} size="md">
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          {errors.submit && (
            <div className="alert alert-danger" role="alert">
              {errors.submit}
            </div>
          )}

          <div className="form-group wparag">
            <label className="control-label">{t('WORKFLOW_CHANGE_REQUEST_NUMBER')}</label>
            <p className="whitespaceprewrap">{workflow?.workflowNo}</p>
          </div>

          <div className="form-group">
            <label htmlFor="qc-approvers">{t('QC_APPROVAL_SEQUENCE')}</label>
            <div
              style={{
                display: 'flex',
                position: 'relative',
                padding: '4px 8px',
                margin: '10px 0',
                background: '#f6f6f6',
                border: '1px solid #d3d4d7',
                color: '#333333',
                borderRadius: '3px',
                alignItems: 'flex-start'
              }}
            >
              <div style={{ width: '50%' }}>
                <Select
                  id="qc-approvers"
                  name="qcApprovers"
                  placeholder={t('SELECT_USERS...')}
                  value={selectedApprovers}
                  options={usersGroupsOptions}
                  onChange={(selectedOptions: any) => {
                    const values = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : []
                    setSelectedApprovers(values)
                    // Clear error when user makes selection
                    if (values.length > 0 && nofAllValue && nofAllValue >= 1) {
                      setErrors({})
                    }
                  }}
                  multi={true}
                  searchable
                  isDisabled={isSubmitting}
                  style={{
                    control: () => ({
                      fontSize: '12px'
                    })
                  }}
                />
                {errors.approvers && (
                  <div className="text-danger" style={{ fontSize: '12px', marginTop: '5px' }}>
                    {errors.approvers}
                  </div>
                )}
              </div>

              <span style={{ marginLeft: '10px', width: '50%' }}>
                <div
                  style={{
                    fontSize: '12px',
                    lineHeight: '25px',
                    marginBottom: '5px',
                    paddingTop: '5px'
                  }}
                >
                  <input
                    id="nofAllValue"
                    type="number"
                    name="nofAllValue"
                    value={nofAllValue !== null && nofAllValue !== undefined ? nofAllValue : ''}
                    style={{
                      width: '26px',
                      height: '25px',
                      border: '1px solid #ccc',
                      borderRadius: '2px',
                      lineHeight: '25px',
                      marginRight: '5px',
                      paddingLeft: '5px'
                    }}
                    onChange={(e) => {
                      const value = e.target.value ? parseInt(e.target.value) : null
                      setNofAllValue(value)
                      // Clear error when valid value entered
                      if (selectedApprovers.length > 0 && value !== null && Number.isInteger(value) && value >= 1) {
                        setErrors({})
                      }
                    }}
                    onBlur={() => {
                      // Validation on blur
                      if (nofAllValue === null || nofAllValue <= 0) {
                        setErrors((prev) => ({ ...prev, nofAllValue: t('SOME_AT_ONCE_ERROR_MSG') }))
                      }
                    }}
                    disabled={isSubmitting}
                    min="1"
                    max="999999999"
                  />
                  {t('TASK_TEAM_N_OF_USER_NEEDED_APPROVE')}
                  {errors.nofAllValue && (
                    <div className="text-danger" style={{ paddingTop: '5px', lineHeight: 'normal', fontSize: '12px' }}>
                      {errors.nofAllValue}
                    </div>
                  )}
                </div>
              </span>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button type="button" className="btn btn-link" onClick={handleCancel} disabled={isSubmitting}>
            {t('CANCEL')}
          </button>
          <button type="submit" className="btn btn-primary text-capitalize" disabled={isSubmitting}>
            {t('SAVE')}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
